import { computed, inject, Injectable, signal } from '@angular/core';
import { SecurityStatus } from '@shared/enums/security-status';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import { PlainObject } from 'odata-query';
import { map, Observable, shareReplay, Subject, tap } from 'rxjs';
import {
  GstarClient,
  SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse,
  SecurityTypeTaxResultItemTypeResponseODataWithMetadataResponse,
} from 'src/app/web-api-client';
import { Collection } from '../../admin/models';
import { taxResultStatusItems } from '../constants/tax-result-status-items';
import { mapSecurityTaxResultFromResponse } from '../mappers/security/map-security-card-tax-result-from-response';
import { mapSecurityTypeResult } from '../mappers/tax-results/map-security-type-result';
import { SecurityCardTaxResultItem } from '../models/security-card-tax-results-item';
import { SecurityTypeResult } from '../models/security-type-result';
import { TaxResultGrid } from '../typing/tax-result-grid';

@Injectable({
  providedIn: 'root',
})
export class TaxResultService {
  private readonly headerService = inject(HeaderService);
  private readonly gstarClient = inject(GstarClient);

  selectedClient = signal<Client | undefined>(undefined);
  selectedTaxResultStatus = signal<SecurityStatus>(
    <SecurityStatus>taxResultStatusItems[0].value
  );
  taxResultByClient$: Subject<Collection<SecurityCardTaxResultItem>> =
    new Subject();
  query = signal<TaxResultGrid>(
    {},
    {
      equal: (_a: TaxResultGrid, _b: TaxResultGrid) => false,
    }
  );
  isFirst = computed<boolean>(() => {
    return Object.entries(this.query()).length === 0;
  });
  taxResultTypes$?: Observable<SecurityTypeResult[]>;

  constructor() {
    this.getTypesResult().subscribe();
  }

  getTypesResult(): Observable<SecurityTypeResult[]> {
    if (!this.taxResultTypes$) {
      this.taxResultTypes$ = this.gstarClient
        .taxresultitemtypesGET(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        )
        .pipe(
          map(
            (
              response: SecurityTypeTaxResultItemTypeResponseODataWithMetadataResponse
            ): SecurityTypeResult[] => {
              return mapSecurityTypeResult(response.value ?? []);
            }
          ),
          shareReplay(1)
        );
    }
    return this.taxResultTypes$;
  }

  clearQuery(): void {
    this.query.set({});
    this.selectedTaxResultStatus.set(SecurityStatus.All);
  }

  updateSecurities(): Observable<Collection<SecurityCardTaxResultItem>> {
    const builtQuery: TaxResultGrid = this.query();
    this.setDefaultQuery(builtQuery);
    this.evaluateSecurityStatus(builtQuery);
    this.evaluateSelectedClient(builtQuery);
    return this.loadSecuritiesTaxResult(builtQuery);
  }

  loadAllSecurities(
    totalRows: number
  ): Observable<Collection<SecurityCardTaxResultItem>> {
    const builtQuery: TaxResultGrid = {
      ...this.query(),
      top: totalRows,
      skip: undefined,
    };

    this.setDefaultQuery(builtQuery);
    this.evaluateSecurityStatus(builtQuery);
    this.evaluateSelectedClient(builtQuery);

    return this.getSecurities(builtQuery);
  }

  updateQuery(query: PlainObject): void {
    if (this.isFirst()) {
      this.firstQuery(query);
    } else {
      this.query.set(query);
    }

    this.updateSecurities().subscribe();
  }

  updateSecurityStatus(securityStatusValue: SecurityStatus): void {
    this.selectedTaxResultStatus.set(securityStatusValue);
  }

  updateClient(client: Client | undefined): void {
    this.selectedClient.set(client);
  }

  private firstQuery(query: TaxResultGrid): void {
    if (this.headerService.selectedClient()) {
      this.selectedClient.set(this.headerService.selectedClient());
    }

    this.query.set(query);
  }

  private setDefaultQuery(query: TaxResultGrid): void {
    query.orderBy = 'securityStatus/securityStatusId asc, modified asc';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));
    filters.set('isActive', true);
    filters.set('entity/client/isActive', true);
    query.filter = Object.fromEntries(filters);
  }

  private evaluateSelectedClient(query: TaxResultGrid): void {
    const clientIdKey = 'entity/client/clientId';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));

    if (this.headerService.selectedClient()) {
      filters.set(clientIdKey, this.headerService.selectedClient()!.id);
    } else {
      if (filters.has(clientIdKey)) {
        filters.delete(clientIdKey);
      }
    }

    query.filter = Object.fromEntries(filters);
  }

  private evaluateSecurityStatus(query: TaxResultGrid): void {
    const taxResultStatusKey = 'securityStatus/securityStatusId';
    const notKey = 'not';
    const filters = new Map(Object.entries(<JSONObject>query.filter ?? {}));

    if (this.selectedTaxResultStatus() === SecurityStatus.All) {
      if (filters.has(taxResultStatusKey)) {
        filters.delete(taxResultStatusKey);
      }
      filters.set(notKey, {
        [taxResultStatusKey]: SecurityStatus.InformationRequired,
      });
    } else {
      if (filters.has(notKey)) {
        filters.delete(notKey);
      }
      filters.set(taxResultStatusKey, this.selectedTaxResultStatus());
    }

    query.filter = Object.fromEntries(filters);
  }

  private loadSecuritiesTaxResult(
    query: TaxResultGrid
  ): Observable<Collection<SecurityCardTaxResultItem>> {
    return this.getSecurities(query).pipe(
      tap((securityTaxResultItems: Collection<SecurityCardTaxResultItem>) => {
        this.taxResultByClient$.next(securityTaxResultItems);
      })
    );
  }

  private getSecurities(
    query?: TaxResultGrid
  ): Observable<Collection<SecurityCardTaxResultItem>> {
    return this.gstarClient
      .securitiesGET2(
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(
          (
            response: SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse
          ) => ({
            total: response.odataCount ?? 0,
            items:
              response.value?.map(response =>
                mapSecurityTaxResultFromResponse(response)
              ) ?? [],
          })
        )
      );
  }
}
